function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId } from "react";
import { ClassNameConfigurator, Input, useAutocomplete } from "@mui/base";
import { Unstable_Popup as Popup } from "@mui/base/Unstable_Popup";
import { autoPlacement as floatingAutoPlacement, size as floatingSize } from "@floating-ui/dom";
import { errorClasses, helperClasses, labelClasses, requiredClasses } from "../../common/classes.js";
import { cn } from "../../common/helpers.js";
import { inputClasses, rootClasses } from "../TextField/index.js";
import { listboxClasses } from "../Select/index.js";
import { optionClasses } from "../SelectOption/index.js";
/**
 * Autocomplete - [API](https://scl.sharp.com/?path=/docs/base-autocomplete)
 */ const Autocomplete = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { label, helperText, error, required, options, size = 'md', className, placeholder } = _param, props = _object_without_properties(_param, [
        "label",
        "helperText",
        "error",
        "required",
        "options",
        "size",
        "className",
        "placeholder"
    ]);
    const { anchorEl, getInputProps, getInputLabelProps, getListboxProps, getOptionProps, getRootProps, groupedOptions, popupOpen, setAnchorEl, value } = useAutocomplete(_object_spread({
        options
    }, props));
    const generatedID = useId();
    var _props_id;
    const inputID = (_props_id = props.id) !== null && _props_id !== void 0 ? _props_id : generatedID;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('space-y-2', className),
        children: [
            label && /*#__PURE__*/ _jsx("label", _object_spread_props(_object_spread({
                htmlFor: inputID
            }, getInputLabelProps()), {
                className: labelClasses({
                    disabled: Boolean(props.disabled),
                    error: Boolean(error)
                }),
                children: label
            })),
            required && /*#__PURE__*/ _jsx("div", {
                "aria-label": "required",
                className: cn(requiredClasses({
                    disabled: Boolean(props.disabled)
                })),
                children: "*"
            }),
            helperText && /*#__PURE__*/ _jsx("div", {
                className: cn(helperClasses({
                    disabled: Boolean(props.disabled)
                })),
                children: helperText
            }),
            /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({}, getRootProps()), {
                ref: setAnchorEl,
                children: /*#__PURE__*/ _jsx(ClassNameConfigurator, {
                    disableDefaultClasses: true,
                    children: /*#__PURE__*/ _jsx(Input, {
                        id: inputID,
                        ref: ref,
                        placeholder: placeholder,
                        slotProps: {
                            root: {
                                className: cn(rootClasses({
                                    error: Boolean(error),
                                    disabled: Boolean(props.disabled),
                                    multiline: false
                                }))
                            },
                            input: _object_spread_props(_object_spread({}, getInputProps()), {
                                className: inputClasses({
                                    size,
                                    disabled: Boolean(props.disabled)
                                })
                            })
                        }
                    })
                })
            })),
            /*#__PURE__*/ _jsx(Popup, {
                open: popupOpen,
                anchor: anchorEl,
                middleware: [
                    floatingAutoPlacement(),
                    floatingSize({
                        apply: ({ rects, elements })=>{
                            Object.assign(elements.floating.style, {
                                minWidth: 'fit-content',
                                width: `${rects.reference.width}px`,
                                zIndex: 1301
                            });
                        }
                    })
                ],
                children: /*#__PURE__*/ _jsxs("ul", _object_spread_props(_object_spread({}, getListboxProps()), {
                    className: listboxClasses(),
                    children: [
                        groupedOptions.map((option, index)=>/*#__PURE__*/ _jsx("li", _object_spread_props(_object_spread({}, getOptionProps({
                                option,
                                index
                            })), {
                                className: optionClasses({
                                    selected: (value === null || value === void 0 ? void 0 : value.value) === option.value,
                                    disabled: false
                                }),
                                children: option.label
                            }))),
                        groupedOptions.length === 0 && /*#__PURE__*/ _jsx("li", {
                            className: optionClasses(),
                            children: "No results"
                        })
                    ]
                }))
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: cn(errorClasses()),
                children: error
            })
        ]
    });
});
export { Autocomplete };
